import { API_CONFIG } from '../constants';

import { Project, ProjectInfo, ApiResponse, BatterySOCResponse, InletPowerResponse, BatteryTempResponse, BatteryCurrentResponse, BatteryVoltageResponse, CCSVoltageResponse, CCSPowerResponse, CCSEVSOCResponse, CCSCurrentResponse, SiteConfiguration, GPSResponse, LoginRequest, LoginResponse, Organization, CreateProjectRequest, ChargersResponse, SiteConfigUnitData, AlarmsResponse, WhitelistResponse, RfidFormData, ProjectRfidCardRequest, UpdateProjectRfidCardRequest, DeleteProjectRfidCardRequest, ChargingSessionResponse, CompaniesResponse, CreateCompanyRequest, UpdateCompanyRequest, Company, AdhocCompaniesResponse, SetAdhocCompanyPriceRequest, SiteConfigProjectResponse, SiteConfigGraph, ProjectAssetsResponse, UsersResponse, OrganizationGPSResponse, MachinesResponse, MachineGPSResponse, MachineBatterySOCResponse, MachineBatteryPowerResponse, MachineBatteryCurrentResponse, MachineCCSPowerResponse, MachineACPowerResponse, MachineTemperatureResponse, MachineEnergyCCSDeliveredResponse, MachineEnergyDeliveredResponse, MachineBatteryTemperatureResponse, MachineInverterPowerResponse, MachineCCSCurrentResponse, MachineAlarmsResponse, MachineSiteConfigResponse } from '../types';


/**
 * Generic API client for making HTTP requests
 */
class ApiClient {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    // Use local proxy in development, CORS proxy services in production
    this.baseUrl = process.env.NODE_ENV === 'development' 
      ? '/api/v1'  // Use local proxy
      : API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }


  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    // Use the configured base URL for both development and production
    const url = `${this.baseUrl}${endpoint}`;
    console.log('Making API request to:', url);
    return this.makeRequest<T>(url, options);
  }

  private getStoredAuthHeader(): string | null {
    try {
      const stored = localStorage.getItem('powersite_auth');
      if (!stored) return null;
      const auth = JSON.parse(stored);
      const token = auth.accessToken || auth.access_token;
      if (!token) return null;

      const tokenType = auth.tokenType || auth.token_type || 'Bearer';
      return `${tokenType} ${token}`;
    } catch {
      return null;
    }
  }

  private isAuthEndpoint(url: string): boolean {
    return (
      url.includes('/auth/login') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/logout')
    );
  }

  private async makeRequest<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    const authHeader = this.isAuthEndpoint(url) ? null : this.getStoredAuthHeader();
    const authHeaders: Record<string, string> = authHeader
      ? { Authorization: authHeader }
      : {};
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...authHeaders,
    };
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          requestHeaders[key] = value;
        });
      } else if (Array.isArray(options.headers)) {
        options.headers.forEach(([key, value]) => {
          requestHeaders[key] = value;
        });
      } else {
        Object.assign(requestHeaders, options.headers);
      }
    }

    try {
      console.log('=== HTTP REQUEST DEBUG ===');
      console.log('Full URL being called:', url);
      console.log('Request method:', options.method || 'GET');
      console.log('Request headers:', {
        ...requestHeaders,
        Authorization: requestHeaders.Authorization ? '[REDACTED]' : undefined,
      });
      console.log('Request body:', options.body);
      console.log('Request options:', options);
      console.log('==========================');
      
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        mode: 'cors', // Explicitly set CORS mode
        credentials: 'omit', // Don't send credentials to avoid CORS issues
        headers: requestHeaders,
      });

      clearTimeout(timeoutId);

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        console.error('Response status:', response.status);
        console.error('Response headers:', Object.fromEntries(response.headers.entries()));
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const responseText = await response.text();
      const data = responseText ? JSON.parse(responseText) : ({} as T);
      console.log('API Response data:', data);
      return {
        data,
        success: true,
      };
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('API Request failed:', error);
      
      let errorMessage = 'Unknown error occurred';
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Request timeout - please check your internet connection';
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Network error - this might be a CORS issue or the API is not accessible. Please check if the API endpoint is correct and accessible.';
        } else if (error.message.includes('CORS')) {
          errorMessage = 'CORS error - the API server needs to allow requests from this domain. Please contact the administrator.';
        } else {
          errorMessage = error.message;
        }
      }
      
      throw new Error(errorMessage);
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    console.log('=== API POST DEBUG ===');
    console.log('Endpoint:', endpoint);
    console.log('Data:', data);
    console.log('Base URL:', this.baseUrl);
    console.log('Full URL will be:', `${this.baseUrl}${endpoint}`);
    console.log('======================');
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
    });
  }
}

// Create a singleton instance
const apiClient = new ApiClient();

/**
 * Project-related API services
 */
export const projectService = {
  async getProjects(orgId: string): Promise<ApiResponse<Project[]>> {
    const params = new URLSearchParams({
      org_id: orgId,
    });
    return apiClient.get<Project[]>(`/projects/?${params.toString()}`);
  },

  async getProject(id: string): Promise<ApiResponse<Project>> {
    return apiClient.get<Project>(`/projects/${id}`);
  },

  async getProjectInfo(projectId: string, orgId: string): Promise<ApiResponse<ProjectInfo>> {
    const params = new URLSearchParams({
      project_id: projectId,
      org_id: orgId,
    });
    return apiClient.get<ProjectInfo>(`/projects/info?${params.toString()}`);
  },

  async createProject(project: CreateProjectRequest): Promise<ApiResponse<Project>> {
    return apiClient.post<Project>('/projects', project);
  },

  async updateProject(id: string, project: Partial<Project>): Promise<ApiResponse<Project>> {
    return apiClient.put<Project>(`/projects/${id}`, project);
  },

  async deleteProject(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/projects/${id}`);
  },
};

/**
 * Battery-related API services
 */
export const batteryService = {
  async getBatterySOC(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<BatterySOCResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<BatterySOCResponse>(`/battery_soc/?${params.toString()}`);
  },

  async getBatteryTemp(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<BatteryTempResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    // Use the correct battery temperature endpoint
    return apiClient.get<BatteryTempResponse>(`/battery_temperature/?${params.toString()}`);
  },

  async getBatteryCurrent(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<BatteryCurrentResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<BatteryCurrentResponse>(`/battery_current/?${params.toString()}`);
  },

  async getBatteryVoltage(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<BatteryVoltageResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<BatteryVoltageResponse>(`/battery_voltage/?${params.toString()}`);
  },
};

/**
 * Site Configuration API services
 */
export const siteConfigService = {
  async getSiteConfigurations(projectId: string): Promise<ApiResponse<SiteConfiguration[]>> {
    return apiClient.get<SiteConfiguration[]>(`/projects/${projectId}/site-configurations`);
  },

  async getSiteConfiguration(id: string): Promise<ApiResponse<SiteConfiguration>> {
    return apiClient.get<SiteConfiguration>(`/site-configurations/${id}`);
  },

  async saveSiteConfiguration(config: Omit<SiteConfiguration, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<SiteConfiguration>> {
    return apiClient.post<SiteConfiguration>('/site-configurations', config);
  },

  async updateSiteConfiguration(id: string, config: Partial<SiteConfiguration>): Promise<ApiResponse<SiteConfiguration>> {
    return apiClient.put<SiteConfiguration>(`/site-configurations/${id}`, config);
  },

  async deleteSiteConfiguration(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/site-configurations/${id}`);
  },

  async getSiteConfigUnit(unitId: string, orgId: string): Promise<ApiResponse<SiteConfigUnitData>> {
    const params = new URLSearchParams({
      unit_id: unitId,
      org_id: orgId,
    });
    
    return apiClient.get<SiteConfigUnitData>(`/siteconfig/unit?${params.toString()}`);
  },

  async getSiteConfigProject(projectId: string, orgId: string): Promise<ApiResponse<SiteConfigProjectResponse>> {
    const params = new URLSearchParams({
      project_id: projectId,
      org_id: orgId,
    });
    
    return apiClient.get<SiteConfigProjectResponse>(`/siteconfig/project?${params.toString()}`);
  },

  async saveSiteConfigProject(orgId: string, projectId: string, graph: SiteConfigGraph): Promise<ApiResponse<any>> {
    const payload = {
      org_id: orgId,
      project_id: projectId,
      graph: graph
    };
    
    return apiClient.post<any>('/siteconfig/projects', payload);
  },

  /** Clears the stored project graph via POST `/siteconfig/projects` (empty nodes/edges). */
  async clearSiteConfigProject(orgId: string, projectId: string): Promise<ApiResponse<any>> {
    return this.saveSiteConfigProject(orgId, projectId, { nodes: [], edges: [] });
  },
};

/**
 * GPS API service for fetching unit positions
 */
export const gpsService = {
  async getUnitGPS(unitId: string, orgId: string): Promise<ApiResponse<GPSResponse>> {
    const params = new URLSearchParams({
      unit_id: unitId,
      org_id: orgId,
    });
    
    // Use the correct GPS endpoint
    return apiClient.get<GPSResponse>(`/gps/?${params.toString()}`);
  },

  async getMachineGPS(unitId: string, orgId: string): Promise<ApiResponse<MachineGPSResponse>> {
    const params = new URLSearchParams({
      unit_id: unitId,
      org_id: orgId,
    });
    
    return apiClient.get<MachineGPSResponse>(`/machine/gps?${params.toString()}`);
  },
};

/**
 * Charger-related API services
 */
export const chargerService = {
  async getCCSVoltage(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<CCSVoltageResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<CCSVoltageResponse>(`/ccs_voltage/?${params.toString()}`);
  },

  async getCCSPower(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<CCSPowerResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<CCSPowerResponse>(`/ccs_power/?${params.toString()}`);
  },

  async getCCSEVSOC(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<CCSEVSOCResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<CCSEVSOCResponse>(`/ccs_ev_soc/?${params.toString()}`);
  },

  async getCCSCurrent(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<CCSCurrentResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<CCSCurrentResponse>(`/ccs_current/?${params.toString()}`);
  },

  async getBatterySOC(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<BatterySOCResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<BatterySOCResponse>(`/battery_soc/?${params.toString()}`);
  },

  async getInletPower(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<InletPowerResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<InletPowerResponse>(`/inlet_power/?${params.toString()}`);
  },

  async getBatteryTemperature(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<BatteryTempResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<BatteryTempResponse>(`/battery_temperature/?${params.toString()}`);
  },

  async getBatteryVoltage(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<BatteryVoltageResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<BatteryVoltageResponse>(`/battery_voltage/?${params.toString()}`);
  },
};


/**
 * Authentication API services
 */
export const authService = {
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    return apiClient.post<LoginResponse>('/auth/login', {
      username: credentials.email,
      password: credentials.password,
    });
  },

  async refreshToken(refreshToken: string): Promise<ApiResponse<LoginResponse>> {
    return apiClient.post<LoginResponse>('/auth/refresh', { refresh_token: refreshToken });
  },

  async logout(): Promise<ApiResponse<void>> {
    return apiClient.post<void>('/auth/logout', {});
  },
};

/**
 * Organization API services
 */
export const organizationService = {
  async getOrganizationByUserId(userId: string): Promise<ApiResponse<Organization>> {
    const params = new URLSearchParams({
      user_id: userId,
    });
    

    // Use the apiClient for consistency and better error handling
    // The full path will be: /api/v1/organization/?user_id={userId}
    // The API returns {organizations: [...]} structure
    const response = await apiClient.get<{ organizations: Organization[] }>(`/organization/?${params.toString()}`);
    
    if (response.success && response.data && response.data.organizations && response.data.organizations.length > 0) {
      // Return the first organization

      return {
        data: response.data.organizations[0],
        success: true,
        message: response.message,
      };
    }
    
    return {
      data: {} as Organization,
      success: false,

      message: response.message || 'No organization found',

    };
  },

  async getAllOrganizationsByUserId(userId: string): Promise<ApiResponse<Organization[]>> {
    const params = new URLSearchParams({
      user_id: userId,
    });
    
    // The API returns {organizations: [...]} structure
    const response = await apiClient.get<{ organizations: Organization[] }>(`/organization/?${params.toString()}`);

    
    if (response.success && response.data && response.data.organizations) {
      return {
        data: response.data.organizations,
        success: true,
        message: response.message,
      };
    }
    
    return {
      data: [],
      success: false,
      message: response.message || 'Failed to fetch organizations',
    };
  },

  async getOrganizationGPS(orgId: string, projectId?: string): Promise<ApiResponse<OrganizationGPSResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
    });
    if (projectId) {
      params.set('project_id', projectId);
    }

    return apiClient.get<OrganizationGPSResponse>(`/organization/gps?${params.toString()}`);
  },
};

/**
 * Chargers API services
 */
export const chargersService = {
  async getChargers(orgId: string): Promise<ApiResponse<ChargersResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
    });
    
    return apiClient.get<ChargersResponse>(`/chargers/?${params.toString()}`);
  },

  async getCharger(orgId: string, unitId: string): Promise<ApiResponse<ChargersResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
    });
    
    return apiClient.get<ChargersResponse>(`/chargers/?${params.toString()}`);
  },

  async getProjectAssets(projectId: string, orgId: string): Promise<ApiResponse<ProjectAssetsResponse>> {
    const params = new URLSearchParams({
      project_id: projectId,
      org_id: orgId,
    });
    
    return apiClient.get<ProjectAssetsResponse>(`/projects/assets/?${params.toString()}`);
  },

  async getAssets(orgId: string): Promise<ApiResponse<ProjectAssetsResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
    });
    
    return apiClient.get<ProjectAssetsResponse>(`/assets/?${params.toString()}`);
  },
};

/**
 * Alarms API services
 */
export const alarmsService = {
  async getAlarms(orgId: string, unitId: string): Promise<ApiResponse<AlarmsResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
    });
    
    return apiClient.get<AlarmsResponse>(`/alarms/?${params.toString()}`);
  },
};

/**
 * Whitelist API services
 */
export const whitelistService = {
  async getWhitelist(orgId: string, unitId: string): Promise<ApiResponse<WhitelistResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
    });
    
    return apiClient.get<WhitelistResponse>(`/whitelist/?${params.toString()}`);
  },

  async getProjectWhitelist(projectId: string, orgId: string): Promise<ApiResponse<WhitelistResponse>> {
    const params = new URLSearchParams({
      project_id: projectId,
      org_id: orgId,
    });
    
    return apiClient.get<WhitelistResponse>(`/projects/whitelist?${params.toString()}`);
  },

  async addRfidCard(orgId: string, unitId: string, rfidData: RfidFormData): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
    });
    
    return apiClient.post<any>(`/whitelist/?${params.toString()}`, rfidData);
  },

  async addProjectRfidCard(rfidData: ProjectRfidCardRequest): Promise<ApiResponse<any>> {
    return apiClient.post<any>('/projects/whitelist', rfidData);
  },

  async updateProjectRfidCard(rfidData: UpdateProjectRfidCardRequest): Promise<ApiResponse<any>> {
    return apiClient.put<any>('/projects/whitelist', rfidData);
  },

  async deleteProjectRfidCard(rfidData: DeleteProjectRfidCardRequest): Promise<ApiResponse<any>> {
    return apiClient.delete<any>('/projects/whitelist', rfidData);
  },
};

/**
 * Charging Session API services
 */
export const chargingSessionService = {
  async getChargingSessions(orgId: string, unitId: string, from?: string, to?: string): Promise<ApiResponse<ChargingSessionResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
    });
    
    if (from) {
      params.append('from', from);
    }
    
    if (to) {
      params.append('to', to);
    }
    
    return apiClient.get<ChargingSessionResponse>(`/charging-session/?${params.toString()}`);
  },

  async getProjectChargingSessions(orgId: string, projectId: string, from?: string, to?: string): Promise<ApiResponse<ChargingSessionResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      project_id: projectId,
    });
    
    if (from) {
      params.append('from', from);
    }
    if (to) {
      params.append('to', to);
    }
    
    return apiClient.get<ChargingSessionResponse>(`/projects/charging-session/?${params.toString()}`);
  },
};

/**
 * Companies API services
 */
export const companiesService = {
  async getCompanies(orgId: string): Promise<ApiResponse<CompaniesResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
    });
    
    return apiClient.get<CompaniesResponse>(`/companies/?${params.toString()}`);
  },

  async createCompany(orgId: string, companyData: CreateCompanyRequest): Promise<ApiResponse<Company>> {
    console.log('=== CREATE COMPANY API DEBUG ===');
    console.log('Company data being sent:', companyData);
    console.log('Data structure:', {
      company_name: companyData.company_name,
      charging_price: companyData.charging_price,
      company_name_type: typeof companyData.company_name,
      charging_price_type: typeof companyData.charging_price,
      rfid_type: Array.isArray(companyData.rfid) ? 'array' : typeof companyData.rfid,
      evcc_id_type: Array.isArray(companyData.evcc_id) ? 'array' : typeof companyData.evcc_id,
      pin_code_type: Array.isArray(companyData.pin_code) ? 'array' : typeof companyData.pin_code,
    });
    console.log('================================');
    
    const params = new URLSearchParams({
      org_id: orgId,
    });

    return apiClient.post<Company>(`/companies/?${params.toString()}`, companyData);
  },

  async updateCompany(companyId: Company['id'], companyData: UpdateCompanyRequest): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`/companies/${companyId}`, companyData);
  },

  async updateCompanyPrice(
    companyId: Company['id'],
    orgId: string,
    chargingPrice: number,
  ): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`/companies/${companyId}`, {
      org_id: orgId,
      charging_price: chargingPrice,
    });
  },

  async deleteCompany(companyId: Company['id']): Promise<ApiResponse<any>> {
    return apiClient.delete<any>(`/companies/${companyId}`);
  },

  async moveUser(orgId: string, body: { rfid: string; to_company_id: number; project_id?: string }): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({ org_id: orgId });
    return apiClient.post<any>(`/companies/move-user?${params.toString()}`, body);
  },

  async getAdhocCompanies(orgId: string, projectId?: string): Promise<ApiResponse<AdhocCompaniesResponse>> {
    const params = new URLSearchParams({ org_id: orgId });
    if (projectId) {
      params.set('project_id', projectId);
    }
    return apiClient.get<AdhocCompaniesResponse>(`/companies/adhoc?${params.toString()}`);
  },

  async setAdhocCompanyPrice(orgId: string, body: SetAdhocCompanyPriceRequest): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({ org_id: orgId });
    return apiClient.put<any>(`/companies/adhoc/price?${params.toString()}`, body);
  },
};

/**
 * Users API services
 */
export const usersService = {
  async getUsers(orgId: string): Promise<ApiResponse<UsersResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
    });
    
    return apiClient.get<UsersResponse>(`/users/?${params.toString()}`);
  },
};

/**
 * Machines/Excavators API services
 */
export const machinesService = {
  async getMachines(orgId: string): Promise<ApiResponse<MachinesResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
    });
    
    return apiClient.get<MachinesResponse>(`/machines/?${params.toString()}`);
  },

  async getMachineChargingSessions(
    orgId: string,
    unitId: string,
    from?: string,
    to?: string
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
    });

    if (from) {
      params.append('from', from);
    }

    if (to) {
      params.append('to', to);
    }

    return apiClient.get<any>(`/machine/charging_sessions?${params.toString()}`);
  },

  async getMachineBatterySOC(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<MachineBatterySOCResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<MachineBatterySOCResponse>(`/machine/battery_soc?${params.toString()}`);
  },

  async getMachineBatteryPower(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<MachineBatteryPowerResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<MachineBatteryPowerResponse>(`/machine/battery_power?${params.toString()}`);
  },

  async getMachineBatteryCurrent(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<MachineBatteryCurrentResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<MachineBatteryCurrentResponse>(`/machine/battery_current?${params.toString()}`);
  },

  async getMachineCCSPower(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<MachineCCSPowerResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<MachineCCSPowerResponse>(`/machine/ccs_power?${params.toString()}`);
  },

  async getMachineACPower(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<MachineACPowerResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<MachineACPowerResponse>(`/machine/ac_power?${params.toString()}`);
  },

  async getMachineTemperature(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<MachineTemperatureResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<MachineTemperatureResponse>(`/machine/temperature?${params.toString()}`);
  },

  async getMachineEnergyCCSDelivered(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<MachineEnergyCCSDeliveredResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<MachineEnergyCCSDeliveredResponse>(`/machine/energy_ccs_delivered?${params.toString()}`);
  },

  async getMachineEnergyDelivered(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<MachineEnergyDeliveredResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<MachineEnergyDeliveredResponse>(`/machine/energy_delivered?${params.toString()}`);
  },

  async getMachineBatteryTemperature(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<MachineBatteryTemperatureResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<MachineBatteryTemperatureResponse>(`/machine/battery_temperature?${params.toString()}`);
  },

  async getMachineInverterPower(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<MachineInverterPowerResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<MachineInverterPowerResponse>(`/machine/inverter_power?${params.toString()}`);
  },

  async getMachineCCSCurrent(
    orgId: string,
    unitId: string,
    from: string,
    to: string
  ): Promise<ApiResponse<MachineCCSCurrentResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
      from: from,
      to: to,
    });
    
    return apiClient.get<MachineCCSCurrentResponse>(`/machine/ccs_current?${params.toString()}`);
  },

  async getMachineAlarms(
    orgId: string,
    unitId: string
  ): Promise<ApiResponse<MachineAlarmsResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
    });
    
    return apiClient.get<MachineAlarmsResponse>(`/machine/alarms?${params.toString()}`);
  },

  async getMachineSiteConfig(
    orgId: string,
    unitId: string
  ): Promise<ApiResponse<MachineSiteConfigResponse>> {
    const params = new URLSearchParams({
      org_id: orgId,
      unit_id: unitId,
    });
    
    return apiClient.get<MachineSiteConfigResponse>(`/machine/site_config?${params.toString()}`);
  },
};

export default apiClient;

